import React, { useState } from "react";
import { Button, Modal, DatePicker, Space, Typography, Card, Checkbox } from "antd";
import { useMemberService } from "../../../services/members/MemberServiceProvider";
import { useMember } from "../../../common/member/MemberContext";
import dayjs, { Dayjs } from "dayjs";
import { notification } from "antd";
import { MemberProfile } from "../../../model/members/MemberProfile";
import MembershipCardGenerator from "../MembershipCardGenerator";
import CreateMembershipTransaction from "./CreateMembershipTransaction";
import PosTransactionModal from "./PosTransactionModal";
import AddPromotionModal from "./AddPromotionModal";
import { PointOfSaleTransaction } from "../../../model/pointofsale/PointOfSaleTransaction";
import { MembershipCategory } from "../../../model/members/MembershipCategory";
import { toDateOnly } from "../../../utils/DateOnly";
import CancellationModal from "./CancellationModal";
import { useClientConfig } from "../../../common/client/ClientConfigProvider";
import { proRataValidTo } from "../../../utils/billingCycle";

interface MemberActionsProps {
  member: MemberProfile;
  pointOfSaleTransactions: PointOfSaleTransaction[];
  membershipCategories: MembershipCategory[];
  onMemberUpdated: () => void;
  // Fired after a POS sale is taken from the profile, so the POS transactions
  // list can refresh without a full member reload.
  onPosTransactionCompleted?: () => void;
  // When true, render as block buttons for the Member Profile V2 "Quick actions"
  // rail (no titled Card wrapper, no card generator — that lives in the header).
  embedded?: boolean;
  // When false, the cancel-membership buttons (Schedule Cancellation / Cancel
  // Immediately) are disabled, e.g. front-of-house. Defaults to true.
  canCancel?: boolean;
}

const MemberActions: React.FC<MemberActionsProps> = ({
  member,
  pointOfSaleTransactions,
  membershipCategories,
  onMemberUpdated,
  onPosTransactionCompleted,
  embedded = false,
  canCancel = true,
}) => {
  const [isActivateModalVisible, setIsActivateModalVisible] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [isScheduleCancellationModalVisible, setIsScheduleCancellationModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [cancelFromToday, setCancelFromToday] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const memberService = useMemberService();
  const { isAdmin } = useMember();
  const { billing } = useClientConfig();

  if (!isAdmin) {
    return null;
  }

  // Default activation valid_to from the client's billing cycle (anchor day +
  // pro-rata cut-off). Mirrors the backend BillingCycleService.
  const calculateDefaultDate = () =>
    proRataValidTo(dayjs(), billing, member.membershipCategory.paymentFrequency);

  const handleActivateClick = () => {
    setSelectedDate(calculateDefaultDate());
    setIsActivateModalVisible(true);
  };

  const handleCancelClick = () => {
    setIsCancelModalVisible(true);
  };

  const handleScheduleCancellationClick = () => {
    setIsScheduleCancellationModalVisible(true);
  };

  const handleActivateConfirm = async () => {
    if (!selectedDate) return;

    setIsActivating(true);
    try {
      await memberService.update({
        ...member,
        validFrom: toDateOnly(member.validFrom),
        validTo: toDateOnly(selectedDate),
        isEnabled: true,
        status: 'Active',
        changeType: 'Activate',
      });

      setIsActivateModalVisible(false);
      onMemberUpdated();
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to activate member",
      });
    } finally {
      setIsActivating(false);
    }
  };

  const handleCancelConfirm = async () => {
    setIsCancelling(true);
    try {

      await memberService.update({
        ...member,
        isEnabled: false,
        validFrom: toDateOnly(member.validFrom),
        validTo: cancelFromToday ? toDateOnly(new Date()) : toDateOnly(member.validTo),
        changeType: 'Cancel',
        status: 'Cancelled'
      });

      notification.success({
        message: "Success",
        description: "Membership has been cancelled",
      });

      setIsCancelModalVisible(false);
      onMemberUpdated();
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to cancel membership",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancellationSuccess = () => {
    setIsScheduleCancellationModalVisible(false);
    onMemberUpdated();
  };

  const actionButtons = (
    <>
      {!embedded && <MembershipCardGenerator member={member} />}
      <CreateMembershipTransaction
        member={member}
        pointOfSaleTransactions={pointOfSaleTransactions}
        membershipCategories={membershipCategories}
        onTransactionUpdated={onMemberUpdated}
        onMemberUpdated={onMemberUpdated}
        block={embedded}
      />
      <PosTransactionModal
        member={member}
        onTransactionCompleted={onPosTransactionCompleted}
        block={embedded}
        triggerTestId="pos-actions-trigger"
      />
      <AddPromotionModal member={member} onApplied={onMemberUpdated} block={embedded} />

      {!member.isEnabled ? (
        <Button
          type="primary"
          block={embedded}
          onClick={handleActivateClick}
          style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          loading={isActivating}
          disabled={isActivating}
        >
          Activate Member
        </Button>
      ) : (
        <Button
          danger
          block={embedded}
          onClick={handleScheduleCancellationClick}
          disabled={!canCancel || !member.isEnabled || member.status === 'Cancelled' || member.status === 'Banned'}
        >
          Schedule Cancellation
        </Button>
      )}

      {member.isEnabled && (
        <Button
          danger
          block={embedded}
          onClick={handleCancelClick}
          disabled={!canCancel || !member.isEnabled || member.status === 'Cancelled' || member.status === 'Banned'}
        >
          Cancel Immediately
        </Button>
      )}
    </>
  );

  const modals = (
    <>
        <Modal
          title="Activate Member"
          open={isActivateModalVisible}
          onOk={handleActivateConfirm}
          onCancel={() => setIsActivateModalVisible(false)}
          okText="Confirm"
          cancelText="Cancel"
          okButtonProps={{
            style: { backgroundColor: "#52c41a", borderColor: "#52c41a" },
            loading: isActivating,
            disabled: isActivating || !selectedDate,
          }}
          cancelButtonProps={{ 
            danger: true,
            disabled: isActivating,
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text>Membership Valid To:</Typography.Text>
            <DatePicker
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </Space>
        </Modal>

        <CancellationModal
          open={isScheduleCancellationModalVisible}
          onCancel={() => setIsScheduleCancellationModalVisible(false)}
          onSuccess={handleCancellationSuccess}
          membershipId={member.membershipId}
          defaultDate={member.validTo ? dayjs(member.validTo) : dayjs()}
        />

        <Modal
          title="Cancel Membership"
          open={isCancelModalVisible}
          onOk={handleCancelConfirm}
          onCancel={() => setIsCancelModalVisible(false)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ 
            danger: true,
            loading: isCancelling,
            disabled: isCancelling,
          }}
          cancelButtonProps={{
            disabled: isCancelling,
          }}
        >
            <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text>
            Are you sure you want to cancel the membership immediately?
          </Typography.Text>
         
          <Checkbox onChange={(e) => setCancelFromToday(e.target.checked)}>
            <Typography.Text>
              Cancel membership from today?
            </Typography.Text> 
          </Checkbox>
          </Space>
      </Modal>
    </>
  );

  if (embedded) {
    return (
      <>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {actionButtons}
        </Space>
        {modals}
      </>
    );
  }

  return (
    <Card title="Member Actions" className="mb-4">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Admin Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Space wrap style={{ justifyContent: 'flex-end' }}>
            {actionButtons}
          </Space>
        </div>
      </div>
      {modals}
    </Card>
  );
};

export default MemberActions;
